// ----- Wave E.5 — PDF/A Compliance Summary -----
//
// Compact "card" rendered above the PDF preview iframe summarizing
// which of the 8 PDF/A checks passed/warned/failed for the current
// document. Mirrors the visual treatment of `<LinksTab />` but
// stripped down to one row per rule.
//
// Data flow:
//   1. Fetches per-document validation results via
//      `v2.documents.getValidationResults(documentId)`.
//   2. Filters to entries whose `ruleName` is in PDFA_RULE_IDS.
//   3. Renders a row per rule, using PDFA_RULE_LABELS for the
//      skim-friendly label and the entry's `message` for the detail.
//
// Empty state ("compliance not yet run") is intentional: the user
// hits the "Validate all" button on the Compiler page to populate.

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { v2, parseApiErrorBody, PDFA_RULE_IDS, PDFA_RULE_LABELS } from '@/api/v2Client';
import { useElementWidth } from '@/hooks/useElementWidth';
import type {
  DocumentValidationResultsResponse,
  ValidationResultEntry,
  PdfARuleId,
} from '@/api/v2Client';

export interface PdfComplianceSummaryProps {
  readonly documentId: string;
}

const wrapperStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: 12,
  marginBottom: 12,
  background: 'var(--surface)',
};

const headerStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

/**
 * phase-compiler-screen-batch item 8 (Sharon, 2026-07-23) — the second column
 * used to be `overflow:hidden; textOverflow:ellipsis; whiteSpace:nowrap` behind
 * a 200px label floor, so in a narrow Inspector EVERY message truncated
 * ("PDF version 1.7 is eCTD-co…") — and that message is the substance
 * ("Document has 5 pages but no bookmarks. Add bookmarks before submission.").
 *
 * Sharon's principle for this product: the exceptional case is loud, the
 * routine case is quiet. So:
 *   - failures and warnings ALWAYS show their full text and are visually
 *     prominent, at any width;
 *   - passing / skipped checks may stay terse on one line, but carry the full
 *     text in `title` — nothing is ever silently clipped;
 *   - below the breakpoint the row STACKS (check name above the muted
 *     explanation) rather than fighting over one line.
 * No horizontal scrolling at any width — explicitly rejected.
 */
const STACK_BELOW_PX = 380;

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 0',
  fontSize: 12,
  borderTop: '1px solid var(--border)',
};

/** Stacked (narrow) row: icon+label on the first line, message beneath. */
const stackedRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 2,
  padding: '6px 0',
  fontSize: 12,
  borderTop: '1px solid var(--border)',
};

const placeholderStyle: CSSProperties = {
  padding: '8px 0',
  fontSize: 12,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
};

const labelStyle: CSSProperties = {
  fontWeight: 500,
  color: 'var(--text)',
  flex: '0 0 auto',
  // Was a hard 200px floor, which is what starved the message column in a
  // narrow Inspector. A basis with permission to shrink keeps the two-column
  // look when there is room without crushing the substance when there isn't.
  flexBasis: 200,
  minWidth: 0,
};

/** Terse one-liner — only ever used for PASSING/skipped checks, always with a title. */
const messageStyle: CSSProperties = {
  color: 'var(--text-muted)',
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Full text, wrapped — used for every failure/warning, and in stacked mode. */
const messageFullStyle: CSSProperties = {
  color: 'var(--text-muted)',
  flex: '1 1 auto',
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

/** A failure/warning message: full text, and loud. */
const messageProminentStyle: CSSProperties = {
  ...messageFullStyle,
  color: 'var(--text)',
  fontWeight: 600,
};

type DisplayStatus = 'pass' | 'warn' | 'error' | 'skip';

function displayStatus(entry: ValidationResultEntry): DisplayStatus {
  if (entry.status === 'skip') return 'skip';
  if (entry.status === 'pass') return 'pass';
  // status === 'fail' — severity decides whether it's an error or a warning
  return entry.severity === 'error' ? 'error' : 'warn';
}

function statusIcon(s: DisplayStatus): string {
  if (s === 'pass') return '✅';
  if (s === 'error') return '❌';
  if (s === 'warn') return '⚠️';
  return '⊘';
}

function statusColor(s: DisplayStatus): string {
  if (s === 'pass') return 'var(--ok, #15803d)';
  if (s === 'error') return 'var(--danger, #b91c1c)';
  if (s === 'warn') return '#92400e';
  return 'var(--text-muted)';
}

function findResult(
  data: DocumentValidationResultsResponse | null,
  ruleId: PdfARuleId,
): ValidationResultEntry | null {
  if (!data) return null;
  return data.results.find((r) => r.ruleName === ruleId) ?? null;
}

export function PdfComplianceSummary({ documentId }: PdfComplianceSummaryProps): ReactElement {
  // phase-compiler-screen-batch item 8 — lay out against the width this panel
  // ACTUALLY has (item 1 made the Inspector's width variable), not the
  // viewport's. Before the first measurement `width` is null; default to the
  // two-column layout so a wide Inspector never flashes the narrow one.
  const { ref: widthRef, width: panelWidth } = useElementWidth<HTMLDivElement>();
  const stacked = panelWidth !== null && panelWidth < STACK_BELOW_PX;
  const [data, setData] = useState<DocumentValidationResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchResults = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await v2.documents.getValidationResults(documentId);
      setData(next);
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void fetchResults();
  }, [fetchResults]);

  // Loading: don't render anything yet. The PreviewTab already has its
  // own iframe loading state; double-spinner is noise.
  if (loading && !data) {
    return (
      <div data-testid="pdf-compliance-summary-loading" style={wrapperStyle}>
        <div style={headerStyle}>PDF/A Compliance</div>
        <div style={placeholderStyle}>Loading compliance results…</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div data-testid="pdf-compliance-summary-error" role="alert" style={wrapperStyle}>
        <div style={headerStyle}>PDF/A Compliance</div>
        <div style={{ ...placeholderStyle, color: 'var(--danger, #b91c1c)' }}>
          Could not load compliance results: {error}
        </div>
      </div>
    );
  }

  // Compute summary counts (driving the header pill colour).
  const entries = PDFA_RULE_IDS.map((id) => ({ id, entry: findResult(data, id) }));
  const anyRun = entries.some((e) => e.entry !== null);

  if (!anyRun) {
    return (
      <div data-testid="pdf-compliance-summary-empty" style={wrapperStyle}>
        <div style={headerStyle}>PDF/A Compliance</div>
        <div data-testid="pdf-compliance-summary-not-run" style={placeholderStyle}>
          Compliance hasn’t been run for this document yet. Use the
          “Validate all” button on the Compiler page to run it.
        </div>
      </div>
    );
  }

  let errorCount = 0;
  let warnCount = 0;
  let passCount = 0;
  let skipCount = 0;
  let notRunCount = 0;
  for (const { entry } of entries) {
    if (!entry) {
      notRunCount++;
      continue;
    }
    const s = displayStatus(entry);
    if (s === 'error') errorCount++;
    else if (s === 'warn') warnCount++;
    else if (s === 'skip') skipCount++;
    else passCount++;
  }
  // navigation-foundation (U03) — a SKIPPED or NOT-RUN check is not a pass.
  // "All checks passed" may ONLY appear when every rule actually passed; when
  // any check was skipped or did not run, the summary says so distinctly rather
  // than implying full coverage.
  const unverified = skipCount + notRunCount;

  const overallStatus: DisplayStatus =
    errorCount > 0 ? 'error' : warnCount > 0 ? 'warn' : unverified > 0 ? 'skip' : 'pass';
  const unverifiedSuffix = unverified > 0 ? `, ${unverified} not fully checked` : '';
  const overallLabel =
    overallStatus === 'pass'
      ? 'All checks passed'
      : overallStatus === 'skip'
        ? `${passCount} passed, ${unverified} not fully checked`
        : `${errorCount} error${errorCount === 1 ? '' : 's'}, ${warnCount} warning${warnCount === 1 ? '' : 's'}${unverifiedSuffix}`;

  return (
    <div data-testid="pdf-compliance-summary" style={wrapperStyle} ref={widthRef}>
      <div style={headerStyle}>
        <span>PDF/A Compliance</span>
        <span
          data-testid="pdf-compliance-summary-overall"
          style={{ color: statusColor(overallStatus), fontWeight: 600 }}
        >
          {statusIcon(overallStatus)} {overallLabel}
        </span>
      </div>
      {entries.map(({ id, entry }) => {
        if (!entry) {
          // Rule didn't run (e.g. it's deactivated in ValidationRules).
          // Still render a row so reviewers see the missing coverage.
          return (
            <div
              key={id}
              data-testid={`pdf-compliance-row-${id}`}
              data-layout={stacked ? 'stacked' : 'columns'}
              data-severity="skip"
              style={stacked ? stackedRowStyle : rowStyle}
              title={`Rule ${id} did not run`}
            >
              <span style={stacked ? { display: 'flex', gap: 6 } : { display: 'contents' }}>
                <span style={{ color: 'var(--text-muted)' }}>{statusIcon('skip')}</span>
                <span style={stacked ? { ...labelStyle, flexBasis: 'auto' } : labelStyle}>
                  {PDFA_RULE_LABELS[id]}
                </span>
              </span>
              <span style={stacked ? messageFullStyle : messageStyle}>(not run)</span>
            </div>
          );
        }
        const s = displayStatus(entry);
        // A failure or a warning is the exceptional case: full text, loud, at
        // any width. A pass may stay terse — but still carries the full text in
        // `title`, so nothing is ever silently clipped.
        const isException = s === 'error' || s === 'warn';
        const message = entry.message ?? '';
        const messageCellStyle = isException
          ? messageProminentStyle
          : stacked
            ? messageFullStyle
            : messageStyle;
        return (
          <div
            key={id}
            data-testid={`pdf-compliance-row-${id}`}
            data-layout={stacked ? 'stacked' : 'columns'}
            data-severity={s}
            style={{
              ...(stacked || isException ? stackedRowStyle : rowStyle),
              ...(isException
                ? { background: 'var(--surface2)', paddingLeft: 6, paddingRight: 6 }
                : null),
            }}
            title={entry.description}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
              <span style={{ color: statusColor(s), flex: '0 0 auto' }}>{statusIcon(s)}</span>
              <span
                style={{
                  ...labelStyle,
                  flexBasis: 'auto',
                  ...(isException ? { color: statusColor(s), fontWeight: 700 } : null),
                }}
              >
                {PDFA_RULE_LABELS[id]}
              </span>
            </span>
            <span
              data-testid={`pdf-compliance-message-${id}`}
              style={messageCellStyle}
              // Only the terse variant can clip, and only for a passing check —
              // give it the full text on hover regardless.
              {...(messageCellStyle === messageStyle ? { title: message } : {})}
            >
              {message}
            </span>
          </div>
        );
      })}
    </div>
  );
}
